import * as cron from "cron";

// Hangfire Cron.Never() is "0 0 31 2 *". JobGenerator.ConvertToHanfireCron drops the
// seconds and year fields, so this Quartz statement becomes exactly that.
export const NEVER_CRON = "0 0 0 31 2 ? *";

export const ALL_DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const WEEKDAYS = ALL_DAYS.slice(0, 5);

export const DAY_NAMES: { [code: string]: { short: string; full: string } } = {
  MON: { short: "Man", full: "mandag" },
  TUE: { short: "Tir", full: "tirsdag" },
  WED: { short: "Ons", full: "onsdag" },
  THU: { short: "Tor", full: "torsdag" },
  FRI: { short: "Fre", full: "fredag" },
  SAT: { short: "Lør", full: "lørdag" },
  SUN: { short: "Søn", full: "søndag" },
};

export type ReportCronMode = "never" | "weekly" | "advanced";

export type ParsedReportCron =
  | { mode: "never" }
  | { mode: "weekly"; days: string[]; hour: string; minute: string }
  | { mode: "advanced" };

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

function splitFields(statement: string): string[] {
  return (statement ?? "").trim().split(/\s+/).filter((f) => f.length);
}

export function isNeverCron(statement: string): boolean {
  const f = splitFields(statement);
  return (f.length === 6 || f.length === 7) && f[3] === "31" && (f[4] === "2" || f[4].toUpperCase() === "FEB");
}

export function parseReportCron(statement: string): ParsedReportCron {
  if (isNeverCron(statement)) return { mode: "never" };

  const f = splitFields(statement);
  if (f.length !== 6 && f.length !== 7) return { mode: "advanced" };
  const [second, min, hour, dom, month, dow, year] = f;
  if (year !== undefined && year !== "*") return { mode: "advanced" };
  if (second !== "0" || month !== "*") return { mode: "advanced" };
  if (!/^\d{1,2}$/.test(min) || parseInt(min) > 59) return { mode: "advanced" };
  if (!/^\d{1,2}$/.test(hour) || parseInt(hour) > 23) return { mode: "advanced" };

  let days: string[];
  if ((dom === "*" && dow === "?") || (dom === "?" && dow === "*")) {
    days = [...ALL_DAYS];
  } else if (dom === "?") {
    const codes = dow.toUpperCase().split(",");
    if (!codes.every((c) => ALL_DAYS.includes(c))) return { mode: "advanced" };
    days = ALL_DAYS.filter((d) => codes.includes(d));
  } else {
    return { mode: "advanced" };
  }

  return { mode: "weekly", days, hour: pad(parseInt(hour)), minute: pad(parseInt(min)) };
}

export function buildWeeklyCron(days: string[], hour: string, minute: string): string {
  if (!days.length) return "";
  const dow = days.length === ALL_DAYS.length ? "*" : ALL_DAYS.filter((d) => days.includes(d)).join(",");
  return `0 ${parseInt(minute)} ${parseInt(hour)} ? * ${dow} *`;
}

export function describeReportCron(statement: string): string {
  const parsed = parseReportCron(statement);
  if (parsed.mode === "never") return "Aldri";
  if (parsed.mode === "advanced") return "Egendefinert uttrykk";

  const time = `kl. ${parsed.hour}:${parsed.minute}`;
  if (parsed.days.length === ALL_DAYS.length) return `Hver dag ${time}`;
  if (parsed.days.length === WEEKDAYS.length && WEEKDAYS.every((d) => parsed.days.includes(d))) {
    return `Hverdager ${time}`;
  }
  const names = parsed.days.map((d) => DAY_NAMES[d].full);
  const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} og ${names[names.length - 1]}` : names[0];
  return `Hver ${list} ${time}`;
}

export function isReportCronValid(statement: string): boolean {
  return isNeverCron(statement) || getNextRuns(statement, 1).length > 0;
}

// Evaluates the statement the way the scheduler does: seconds and year are ignored.
export function getNextRuns(statement: string, n: number = 3): Date[] {
  if (isNeverCron(statement)) return [];
  const f = splitFields(statement);
  if (f.length !== 6 && f.length !== 7) return [];
  if (f.length === 7 && f[6] !== "*") return [];
  const sixFields = f.slice(0, 6).map((v) => (v === "?" ? "*" : v)).join(" ");
  try {
    const dates = new cron.CronJob(sixFields, () => {}).nextDates(n);
    return (Array.isArray(dates) ? dates : [dates]).map((d: any) => d.toDate());
  } catch (e) {
    return [];
  }
}
